import React from "react";
import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";

/**
 * Canonical production domain. Used for canonical URLs, og:url,
 * Schema.org, and the static sitemap.xml.
 *
 * NOTE: the preview env runs under cyber-forefront.preview.emergentagent.com,
 * but every public-facing canonical/Schema URL must point at the production
 * apex domain to consolidate SEO signals before launch.
 */
export const SITE_DOMAIN = "https://cybersecurityforefront.com";
export const SITE_NAME = "Cybersecurity Forefront";
export const DEFAULT_OG_IMAGE = `${SITE_DOMAIN}/og-hero-image.png`;
export const DEFAULT_DESCRIPTION =
    "Chat-first firewall configurator (Fortinet, WatchGuard, Zyxel, pfSense), Client IPAM, and free compliance tools — built for MSPs and security engineers.";

/**
 * <SEO /> — per-page Helmet wrapper.
 *
 * Props:
 *  - title          : page title; will be templated as "{title} | Cybersecurity Forefront"
 *                     unless `rawTitle` is true.
 *  - description    : meta description + OG/Twitter description.
 *  - canonicalPath  : path-only override (e.g. "/about"); defaults to current pathname.
 *  - canonicalUrl   : full URL override (wins over canonicalPath).
 *  - image          : absolute OG image URL (defaults to the static hero PNG).
 *  - noindex        : if true, emits <meta name="robots" content="noindex, nofollow">.
 *  - schema         : JSON-LD object (or array of objects) to inject as a
 *                     <script type="application/ld+json">.
 *  - ogType         : OG type (default "website").
 *  - rawTitle       : if true, use `title` verbatim without templating.
 */
export const SEO = ({
    title,
    description = DEFAULT_DESCRIPTION,
    canonicalPath,
    canonicalUrl,
    image = DEFAULT_OG_IMAGE,
    noindex = false,
    schema,
    ogType = "website",
    rawTitle = false,
}) => {
    const location = useLocation();
    const path = canonicalPath || location.pathname || "/";
    const canonical = canonicalUrl || `${SITE_DOMAIN}${path}`;
    const fullTitle = title
        ? rawTitle
            ? title
            : `${title} | ${SITE_NAME}`
        : `${SITE_NAME} — Firewall, IPAM & Compliance Tools`;

    return (
        <Helmet prioritizeSeoTags>
            <title>{fullTitle}</title>
            <meta name="description" content={description} />
            <link rel="canonical" href={canonical} />
            <link rel="alternate" hrefLang="en" href={canonical} />
            <link rel="alternate" hrefLang="x-default" href={canonical} />
            {noindex ? (
                <meta name="robots" content="noindex, nofollow" />
            ) : (
                <meta name="robots" content="index, follow, max-image-preview:large" />
            )}
            {/* Open Graph */}
            <meta property="og:type" content={ogType} />
            <meta property="og:site_name" content={SITE_NAME} />
            <meta property="og:title" content={fullTitle} />
            <meta property="og:description" content={description} />
            <meta property="og:url" content={canonical} />
            <meta property="og:image" content={image} />
            <meta property="og:image:width" content="1200" />
            <meta property="og:image:height" content="630" />
            <meta property="og:image:alt" content="Cybersecurity Forefront — chat-first firewall configurator and Client IPAM" />
            <meta property="og:locale" content="en_US" />
            {/* Twitter */}
            <meta name="twitter:card" content="summary_large_image" />
            <meta name="twitter:title" content={fullTitle} />
            <meta name="twitter:description" content={description} />
            <meta name="twitter:image" content={image} />
            {/* JSON-LD */}
            {schema &&
                (Array.isArray(schema) ? schema : [schema]).map((s, i) => (
                    <script
                        key={i}
                        type="application/ld+json"
                        // Helmet does not render children of <script>; we pass via innerHTML-like
                        // technique by serializing into the text content.
                    >
                        {JSON.stringify(s)}
                    </script>
                ))}
        </Helmet>
    );
};

export default SEO;
